//---------------------------------------------------------
// Power-button settle gate.
//
// After an accepted power call the tile disables Power off / Power on /
// Reboot until this instance's existing health sample shows the action
// has settled, or until POWER_SETTLE_MS if that sample never arrives.
// Health is not polled here: the card already receives IInstanceHealth
// (refetchInterval is false on the hook). No Linode poll, no new API.
//---------------------------------------------------------
import {SoftPowerAction} from 'connector/oam/oam';

export const POWER_SETTLE_MS = 90_000;

export type PowerSettleState = {
	action: SoftPowerAction;
	acceptedAt: number;
	/** Boot/reboot: a post-accept (or pre-accept) sample was Down, so a later Healthy means "up again". */
	sawDown: boolean;
};

export type PowerHealthSample = {
	isHealthy: boolean;
	lastChecked: number;
};

export function powerConfirmLabel(action: SoftPowerAction): string {
	switch (action) {
		case 'shutdown':
			return 'Power off';
		case 'boot':
			return 'Power on';
		case 'reboot':
			return 'Reboot';
	}
}

export function powerAcceptedMessage(action: SoftPowerAction): string {
	switch (action) {
		case 'shutdown':
			return 'Power off accepted';
		case 'boot':
			return 'Power on accepted';
		case 'reboot':
			return 'Reboot accepted';
	}
}

/** Busy / still-in-progress power failure. Does not match unrelated errors. */
export function isPowerStillSettling(err: {httpStatus?: number; code?: string; error?: string}): boolean {
	if (err.httpStatus === 409) return true;
	if (err.code === 'LINODE_POWER_BUSY') return true;
	return (err.error ?? '').includes('Linode power request failed');
}

export function initialPowerSettle(action: SoftPowerAction, acceptedAt: number, wasHealthy: boolean | null): PowerSettleState {
	return {
		action,
		acceptedAt,
		sawDown: action !== 'shutdown' && wasHealthy === false,
	};
}

/**
 * Next settle state. null means re-enable the three power buttons.
 * Same reference means nothing changed.
 */
export function reducePowerSettle(
	settle: PowerSettleState,
	health: PowerHealthSample | null | undefined,
	now: number,
): PowerSettleState | null {
	if (now - settle.acceptedAt >= POWER_SETTLE_MS) return null;
	if (!health || health.lastChecked <= settle.acceptedAt) return settle;

	if (!health.isHealthy) {
		if (settle.action === 'shutdown') return null;
		if (!settle.sawDown) return {...settle, sawDown: true};
		return settle;
	}

	// Healthy sample taken after the accept.
	if (settle.action === 'shutdown') return settle;
	if (settle.sawDown) return null;
	return settle;
}

//---------------------------------------------------------
// Tile power actions from Linode's own status string.
// Health Status Healthy/Down is not running/offline.
//---------------------------------------------------------

export const REBOOT_DISABLED_TOOLTIP = 'Available when the instance is running.';

export type TilePowerKind = 'off' | 'on';

export type TilePowerActions = {
	showPower: boolean;
	power: TilePowerKind | null;
	powerDisabled: boolean;
	powerTooltip: string;
	showReboot: boolean;
	rebootDisabled: boolean;
	rebootTooltip: string;
};

const HIDDEN_POWER: TilePowerActions = {
	showPower: false,
	power: null,
	powerDisabled: true,
	powerTooltip: '',
	showReboot: false,
	rebootDisabled: true,
	rebootTooltip: '',
};

function isTransitional(status: string): boolean {
	return status === 'booting' || status === 'shutting_down' || status === 'rebooting';
}

/**
 * Which power controls the tile shows.
 * hasLinodeBinding false hides Power off, Power on, and Reboot.
 * running: Power off + Reboot enabled (tooltip Power off), unless a request is in flight.
 * offline: Power on, Reboot greyed with REBOOT_DISABLED_TOOLTIP.
 * booting / shutting_down / rebooting: keep the last power button and disable both
 * until settle finishes. With no previous button, shutting_down and rebooting are
 * Power off disabled (the instance was running). Only booting is Power on disabled.
 * Any other string, or null: no power action (not a disabled Power off).
 */
export function tilePowerActions(args: {
	hasLinodeBinding: boolean;
	linodeStatus: string | null | undefined;
	lastPower: TilePowerKind | null;
	powerPending: boolean;
	/** True while the existing 90s settle (or Still settling) is in progress. Transitional buttons stay disabled either way. */
	settling: boolean;
}): TilePowerActions {
	if (!args.hasLinodeBinding) return HIDDEN_POWER;
	const status = args.linodeStatus ?? null;
	if (status === null || status === '') return HIDDEN_POWER;

	if (status === 'running') {
		const locked = args.powerPending;
		return {
			showPower: true,
			power: 'off',
			powerDisabled: locked,
			powerTooltip: 'Power off',
			showReboot: true,
			rebootDisabled: locked,
			rebootTooltip: 'Reboot',
		};
	}
	if (status === 'offline') {
		return {
			showPower: true,
			power: 'on',
			powerDisabled: args.powerPending,
			powerTooltip: 'Power on',
			showReboot: true,
			rebootDisabled: true,
			rebootTooltip: REBOOT_DISABLED_TOOLTIP,
		};
	}
	if (isTransitional(status)) {
		let power: TilePowerKind;
		if (args.lastPower) power = args.lastPower;
		else if (status === 'booting') power = 'on';
		else power = 'off';
		// Disabled for the whole transitional window. That covers the existing
		// 90s settle, including Still settling. Clearing settle does not enable
		// a button while Linode still reports booting, shutting_down, or rebooting.
		const stillSettling = args.settling;
		let powerDisabled = true;
		if (stillSettling) powerDisabled = true;
		return {
			showPower: true,
			power,
			powerDisabled,
			powerTooltip: power === 'off' ? 'Power off' : 'Power on',
			showReboot: true,
			rebootDisabled: true,
			rebootTooltip: REBOOT_DISABLED_TOOLTIP,
		};
	}
	return HIDDEN_POWER;
}
